import type {
    BoardCardShortcut,
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "./Board.types";
import { getCellEntries, type BoardCells } from "./boardCells";
import { getLaneLabel } from "./boardCollapse";
import type { BoardKeyboardDirection } from "./boardDropTargets";
import { locateBoardEntry } from "./boardMove";
import { getBoardSelectionCopy } from "./boardSelectionCopy";
import { BOARD_PLACE_KEY_LABEL } from "./boardSensors";

const OPEN_INSTRUCTIONS = "Press Enter to open the card.";
const MOVE_INSTRUCTIONS =
    "To move it, press Space to pick it up, use the arrow keys to move it between columns and positions, then press Space or Enter to drop it. Press Escape to cancel.";

export const BOARD_DRAG_INSTRUCTIONS = `${OPEN_INSTRUCTIONS} ${MOVE_INSTRUCTIONS}`;

/** `cell` names where Home and End stay: "column", or "column and lane". */
const getNavigationInstructions = (cell: string) =>
    `Use the arrow keys to move between cards, Home and End to go to the first or last card in this ${cell}, and Page Up or Page Down to jump further.`;
const CREATE_INSTRUCTIONS =
    "Press N to create a card below it, or Shift+N to create one above.";

interface BoardKeyboardCopyArgs {
    isDraggable: boolean;
    canAdd: boolean;
    hasLanes: boolean;
    canSelect: boolean;
    shortcuts: readonly BoardCardShortcut[];
}

/**
 * What the cards describe to screen readers (cards that open on Enter get
 * the open sentence, the rest don't), and the hint shown on first keyboard
 * focus. `canAdd`: N and Shift+N create cards on this board.
 */
export const getBoardKeyboardCopy = ({
    isDraggable,
    canAdd,
    hasLanes,
    ...selectionArgs
}: BoardKeyboardCopyArgs) => {
    const selection = getBoardSelectionCopy(selectionArgs);
    const getInstructions = (open: readonly string[]) =>
        [
            getNavigationInstructions(hasLanes ? "column and lane" : "column"),
            ...open,
            ...(isDraggable ? [MOVE_INSTRUCTIONS] : []),
            ...(canAdd ? [CREATE_INSTRUCTIONS] : []),
            ...selection.instructions,
        ].join(" ");
    return {
        instructions: {
            clickable: getInstructions([OPEN_INSTRUCTIONS]),
            plain: getInstructions([]),
        },
        canAdd,
        hintKeys: selection.hintKeys,
        hint: canAdd
            ? "Use arrow keys to move between cards. Press N to create one below."
            : "Use arrow keys to move between cards.",
    };
};

export type BoardKeyboardCopy = ReturnType<typeof getBoardKeyboardCopy>;

interface BoardSlotDescription {
    itemLabel: string;
    /** The column, plus the lane when there are lanes ("Review, Harbor Dental"). */
    columnTitle: string;
    /** 1-based position of the card in its column. */
    position: number;
    total: number;
}

export type BoardAnnouncementMessage = (slot: BoardSlotDescription) => string;

export interface BoardCellNames {
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
}

/** The column's title; its id if it isn't on the board. */
export const getColumnTitle = (
    columns: readonly BoardColumnDef[],
    columnId: string,
) => columns.find((column) => column.id === columnId)?.title ?? columnId;

/** "Review", or "Review, Harbor Dental" on a board with lanes. */
export const getCellTitle = (
    { columns, lanes }: BoardCellNames,
    { columnId, laneId }: BoardPlacement,
) => {
    const columnTitle = getColumnTitle(columns, columnId);
    const lane = lanes?.find(({ id }) => id === laneId);
    return lane === undefined
        ? columnTitle
        : `${columnTitle}, ${getLaneLabel(lane)}`;
};

interface BoardAnnouncementArgs<TItem> extends BoardCellNames {
    cells: BoardCells<TItem>;
    placementKey: string;
    getItemLabel: (item: TItem) => string;
    toMessage: BoardAnnouncementMessage;
}

/** `toMessage` for the copy at `placementKey` in `cells`; empty if it's gone. */
export const getBoardAnnouncement = <TItem>({
    cells,
    placementKey,
    getItemLabel,
    toMessage,
    ...names
}: BoardAnnouncementArgs<TItem>) => {
    const location = locateBoardEntry(cells, placementKey);
    if (location === null) {
        return "";
    }
    const { item, placement } = location.entry;
    return toMessage({
        itemLabel: getItemLabel(item),
        columnTitle: getCellTitle(names, placement),
        position: location.index + 1,
        total: getCellEntries(cells, placement).length,
    });
};

export const announcePickUp = ({
    itemLabel,
    columnTitle,
}: BoardSlotDescription) => `Picked up ${itemLabel} in ${columnTitle}.`;

export const announceSlot = ({
    columnTitle,
    position,
    total,
}: BoardSlotDescription) =>
    `Moved to ${columnTitle}, position ${position} of ${total}.`;

/** The place key is held where `canPlaceAt` refuses: the card goes by the sort. */
export const announceSortedSlot =
    (placeRefusal: string): BoardAnnouncementMessage =>
    (slot) =>
        `${announceSlot(slot)} ${placeRefusal}`;

/** A held step that skipped refused spots: the step's message, then why. */
export const announceSkippedPlace =
    <TSlot>(toMessage: (slot: TSlot) => string, skippedRefusal: string) =>
    (slot: TSlot) =>
        `${toMessage(slot)} ${skippedRefusal}`;

/** A held up or down step with no spot left that way that `canPlaceAt` allows. */
export const announceRefusedPlace = (placeRefusal: string) =>
    `Can't place the card any further. ${placeRefusal}`;

export const announceDropToColumn = ({
    itemLabel,
    columnTitle,
}: BoardSlotDescription) => `Moved ${itemLabel} to ${columnTitle}.`;

export const announceDropInColumn = ({
    itemLabel,
    columnTitle,
    position,
    total,
}: BoardSlotDescription) =>
    `Moved ${itemLabel} to position ${position} of ${total} in ${columnTitle}.`;

export const announceReturn = ({
    itemLabel,
    columnTitle,
}: BoardSlotDescription) => `${itemLabel} returned to ${columnTitle}.`;

export const announceCancel = (slot: BoardSlotDescription) =>
    `Move canceled. ${announceReturn(slot)}`;

export type BoardRefusalMessage = (refusal: BoardRefusalDescription) => string;

interface BoardRefusalDescription {
    itemLabel: string;
    columnTitle: string;
    reason: string;
}

export const announceRefusedSlot = ({
    columnTitle,
    reason,
}: BoardRefusalDescription) => `Can't move to ${columnTitle}. ${reason}`;

export const announceRefusedDrop = ({
    itemLabel,
    columnTitle,
    reason,
}: BoardRefusalDescription) =>
    `Can't move ${itemLabel} to ${columnTitle}. ${reason}`;

const EDGE_SIDES: Record<BoardKeyboardDirection, string> = {
    left: "to the left of",
    right: "to the right of",
    up: "above",
    down: "below",
};

interface KeyboardEdgeAnnouncementArgs extends BoardCellNames {
    placement: BoardPlacement;
    direction: BoardKeyboardDirection;
}

/**
 * An arrow key with nowhere to go: "No column to the right of Review.",
 * "No lane below Harbor Dental.", or "No position above in Review." on a
 * board without lanes.
 */
export const getKeyboardEdgeAnnouncement = ({
    columns,
    lanes,
    placement: { columnId, laneId },
    direction,
}: KeyboardEdgeAnnouncementArgs) => {
    const columnTitle = getColumnTitle(columns, columnId);
    const side = EDGE_SIDES[direction];
    if (direction === "left" || direction === "right") {
        return `No column ${side} ${columnTitle}.`;
    }
    const lane = lanes?.find(({ id }) => id === laneId);
    return lane === undefined
        ? `No position ${side} in ${columnTitle}.`
        : `No lane ${side} ${getLaneLabel(lane)}.`;
};

interface CardEdgeAnnouncementArgs extends BoardCellNames {
    placement: BoardPlacement;
    /** `first` and `last`: Home, End or a page key already at that end. */
    direction: BoardKeyboardDirection | "first" | "last";
    /** Columns lie that way, with no card in this lane. */
    hasColumn: boolean;
}

/**
 * Focus with nowhere to go: "No card above in Prep.", "No card below in
 * Prep, Harbor Dental.", "No column to the right of Deliver.", "No card
 * to the right of Prep." when the columns there are empty, or "Already at
 * the last card in Prep." for End and the page keys.
 */
export const getCardEdgeAnnouncement = ({
    placement,
    direction,
    hasColumn,
    ...names
}: CardEdgeAnnouncementArgs) => {
    if (direction === "first" || direction === "last") {
        return `Already at the ${direction} card in ${getCellTitle(names, placement)}.`;
    }
    const side = EDGE_SIDES[direction];
    if (direction === "up" || direction === "down") {
        return `No card ${side} in ${getCellTitle(names, placement)}.`;
    }
    const columnTitle = getColumnTitle(names.columns, placement.columnId);
    return `No ${hasColumn ? "card" : "column"} ${side} ${columnTitle}.`;
};

/** N or Shift+N on a card whose cell takes no new cards from the keyboard. */
export const CANT_CREATE_HERE = "Can't create a card here.";

/** Up or down in a sorted column without lanes: there are no slots to step through. */
export const announceSortedColumn = (columnTitle: string) =>
    `Can't reorder cards in ${columnTitle}. The column is sorted.`;

/** Follows `announceSortedColumn` on a board with `canPlaceBetween`. */
export const PLACE_BY_HAND_INSTRUCTIONS = `Hold ${BOARD_PLACE_KEY_LABEL} and press an arrow key to place it by hand.`;

export const announceLocked = (
    itemLabel: string,
    reason: string | undefined,
) =>
    reason === undefined
        ? `${itemLabel} can't be moved.`
        : `${itemLabel} can't be moved. ${reason}`;
